import React, { useCallback, useEffect, useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { Content } from '@eika/engine-view';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import MultiformWrapper from '../../components/transaction/MultiformWrapper/MultiformWrapper';
import { PaymentActionType } from '../../components/transaction/Payment/form/actions';
import paymentReducer from '../../components/transaction/Payment/form/reducer';
import { paymentInitialState } from '../../components/transaction/Payment/form/state';
import useFormContext, { PaymentFormContext } from '../../components/transaction/Payment/form/useFormContext';
import useValidate from '../../components/transaction/Payment/form/validate';
import { PaymentAndTransferForm } from '../../components/transaction/PaymentAndTransferForm';
import useAmount from '../../hooks/queryparams/useAmount';
import useQueryParam from '../../hooks/queryparams/useQueryParam';
import useDebounce from '../../hooks/useDebounce';
import usePaymentAccount from '../../hooks/usePaymentAccount';
import useReceiverAccount from '../../hooks/useReceiverAccount';
import useRedirectOnValidUrl from '../../hooks/useRedirectOnValidUrl/useRedirectOnValidUrl';
import useTriggerEffect from '../../hooks/useTriggerEffect';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import usePaymentOrTransferViewType from './usePaymentOrTransferViewType';

import './PaymentAndTransferPage.scss';

interface Props {
    noTabs?: boolean;
}

const PaymentAndTransferPage = ({ noTabs = false }: Props) => {
    const { handleCancelSigningFlow, dispatch, signingStatus } = useSigningContext();
    const { handleChange } = useFormContext();
    const amount = useAmount();
    const account = usePaymentAccount();
    const receiver = useReceiverAccount();
    const kid = useQueryParam('kid');
    const view = usePaymentOrTransferViewType();
    const [resetDisplay, setResetDisplay] = useState(false);

    useEffect(() => {
        if (amount) {
            handleChange({ type: PaymentActionType.AMOUNT, payload: amount });
        }
    }, [handleChange, amount]);
    useEffect(() => {
        if (kid) {
            handleChange({ type: PaymentActionType.SET_KID, payload: { value: kid } });
        }
    }, [handleChange, kid]);
    useEffect(() => {
        if (receiver) {
            handleChange({ type: PaymentActionType.TO_ACCOUNT_SELECTED, payload: receiver });
        }
    }, [handleChange, receiver]);
    useEffect(() => {
        if (account) {
            handleChange({ type: PaymentActionType.FROM_ACCOUNT_SELECTED, payload: account.accountNumber });
        }
    }, [account, handleChange]);

    const resetPaymentForm = useCallback(() => {
        dispatch({ type: Actions.RESET });
        handleCancelSigningFlow();
        handleChange({ type: PaymentActionType.RESET_WITH_FROM_ACCOUNT, payload: account?.accountNumber ?? undefined });
    }, [account, dispatch, handleCancelSigningFlow, handleChange]);

    useEffect(() => {
        if (resetDisplay) {
            resetPaymentForm();
            setResetDisplay(false);
        }
    }, [resetPaymentForm, resetDisplay]);

    const redirectAfterCompletion = useRedirectOnValidUrl();

    const debouncedPaymentCompleted = useDebounce(() => {
        setResetDisplay(true);
        redirectAfterCompletion();
    }, 1500);

    useTriggerEffect(
        signingStatus === 'COMPLETED',
        () => {
            debouncedPaymentCompleted();
        },
        [signingStatus],
    );

    return (
        <Content>
            <section className="nettbank-transactions__container no-padding cannot-close active">
                <PaymentAndTransferForm
                    defaultFocusActive
                    defaultForm={view}
                    wrapInContext={['transfer']}
                    account={account}
                    transferForm={<MultiformWrapper transactionType="transfer" account={account} type="page" />}
                    paymentForm={
                        <MultiformWrapper
                            transactionType="payment"
                            account={account}
                            type="page"
                            lockReceiver={!!receiver}
                            lockNote={!!kid}
                            noTabs={noTabs}
                        />
                    }
                />
            </section>
        </Content>
    );
};

export default ({ noTabs }: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.PAYMENT}>
        <PaymentFormContext
            validateHook={useValidate}
            reducer={paymentReducer}
            initialState={{
                ...paymentInitialState,
                paymentDate: getZonedDate(),
            }}
        >
            <PaymentAndTransferPage noTabs={noTabs} />
        </PaymentFormContext>
    </SigningContextProvider>
);
